import { Translation } from "../hooks/useTranslations";

const es: Translation = {
  drivers: {
    edit: "Editar conductor",
    new: "Nuevo conductor",
    singular: "Conductor",
    plural: "Conductores",
    delete: "Eliminar conductor",

    questions: {
      delete: "¿Quieres eliminar realmente este conductor?",
    },
  },

  subsidiaries: {
    delete: "Eliminar filial",
    edit: "Editar filial",
    new: "Nueva filial",
    singular: "Filial",
    plural: "Filiales",
    versions: "Versiones de la filial",

    questions: {
      delete: "¿Quieres eliminar realmente esta filial?",
    },

    errors: {
      notMatchingSubsidiaryFound: "No se encontró ninguna filial coincidente.",
      nameAlreadyExists: "Nombre de la filial ya utilizado",
      countryISOCodeAlreadyExists: "El código ISO del país especificado ya está definido para otra filial.",
      inUseByCars: "La filial todavía está utilizada por coches y no puede eliminarse.",
      inUseByCarOrders: "La filial todavía está utilizada por una compra de coche y no puede eliminarse.",
      missingVatServiceCode: "Falta el código de IVA de servicios de la filial",
      missingVatDeductionCode: "Falta el código de deducción de IVA de la filial",
      missingCountryIsoCode: "Falta el código ISO del país de la filial",
      missingSemiramisOrganizationCode: "Falta el código de organización Semiramis de la filial",
    },
  },

  manufacturers: {
    delete: "Eliminar fabricante",
    edit: "Editar fabricante",
    new: "Nuevo fabricante",
    singular: "Fabricante",
    plural: "Fabricantes",
    versions: "Versiones del fabricante",

    questions: {
      delete: "¿Quieres eliminar realmente este fabricante?",
    },

    errors: {
      nameAlreadyUsed: "Ya existe un fabricante con el mismo nombre.",
      inUseByCars: "El fabricante todavía está utilizado por coches y no puede eliminarse.",
      inUseByCarOrders: "El fabricante todavía está utilizado por una compra de coche y no puede eliminarse.",
    },
  },

  cars: {
    all: "Todos los coches",
    delete: "Eliminar coche",
    edit: "Editar coche",
    new: "Nuevo coche",
    singular: "Coche",
    plural: "Coches",
    versions: "Versiones del coche",

    questions: {
      delete: "¿Quieres eliminar realmente este coche?",
    },

    errors: {
      vinAlreadyUsed: "Ya existe un coche con el número de bastidor especificado.",
      carReadNotAllowed: "Lectura de coche no permitida",
    },
  },

  suppliers: {
    delete: "Eliminar proveedor",
    edit: "Editar proveedor",
    new: "Nuevo proveedor",
    singular: "Proveedor",
    plural: "Proveedores",
    versions: "Versiones del proveedor",

    questions: {
      delete: "¿Quieres eliminar realmente este proveedor?",
    },

    errors: {
      nameAlreadyUsed: "Ya existe un proveedor con el mismo nombre.",
      inUseByCarOrders: "El proveedor todavía está utilizado por compras de coches y no puede eliminarse.",
    },
  },

  carOrders: {
    all: "Todas las compras de coches",
    delete: "Eliminar compra de coche",
    edit: "Editar compra de coche",
    new: "Nueva compra de coche",
    singular: "Compra de coche",
    plural: "Compras de coches",
    versions: "Versiones de la compra de coche",

    questions: {
      delete: "¿Quieres eliminar realmente esta compra de coche?",
      deleteLinkedPlanning:
        'Esta compra de coche está vinculada a una planificación de coche. Su estado se restablecerá a "Por pedir".',
    },
    errors: {
      carOrderDateAfterSoldDate: "La fecha de compra del coche no puede ser posterior a la fecha de venta.",
    },
  },

  carPlates: {
    delete: "Eliminar matrícula",
    edit: "Editar matrícula",
    new: "Nueva matrícula",
    singular: "Matrícula",
    plural: "Matrículas",
    versions: "Versiones de la matrícula",

    questions: {
      delete: "¿Quieres eliminar realmente esta matrícula?",
    },

    errors: {
      plateOverlapPeriodSub: "La matrícula ya está utilizada por otro coche en la misma filial.",
      plateOverlapPeriodCar: "El período especificado se superpone con otra matrícula.",
      carPlateOutOfSubsidiaryPeriod: "El período de la matrícula no está dentro del período de la filial.",
    },
  },

  carDrivers: {
    delete: "Eliminar conductor del coche",
    edit: "Editar conductor del coche",
    new: "Nuevo conductor del coche",
    singular: "Conductor del coche",
    plural: "Conductores del coche",
    versions: "Versiones del conductor del coche",
    sendMileageEmail: "Enviar email de kilometraje al nuevo conductor",
    mileageEmailFirstAssignment: "El email no se puede enviar: es el primer conductor de este coche.",
    mileageEmailNotificationsDisabled: (flag: string) =>
      `El email no se puede enviar: el conductor tiene activada la opción "${flag}".`,
    mileageEmailNoEmail: "El email no se puede enviar: este conductor no tiene dirección de email.",

    questions: {
      delete: "¿Quieres eliminar realmente este conductor del coche?",
    },

    errors: {
      overlapsPeriod: "El período especificado se superpone con otro conductor del coche.",
      overlapsPeriodPool: "El período especificado se superpone con otro del parque de coches.",
      carDriverOutOfSubsidiaryPeriod: "El período del conductor no está dentro del período de la filial.",
      delegationLanguageNotFound: "El idioma seleccionado no está disponible para las delegaciones.",
      delegationSubsidiaryNotConfigured:
        "Los datos de la delegación no están configurados para el código de organización Semiramis de la filial.",
    },
  },

  carLeasings: {
    delete: "Eliminar leasing de coche",
    edit: "Editar leasing de coche",
    conclude: "Concluir leasing de coche",
    new: "Nuevo leasing de coche",
    singular: "Leasing de coche",
    plural: "Leasings de coches",
    versions: "Versiones del leasing de coche",

    questions: {
      delete: "¿Quieres eliminar realmente este leasing de coche?",
      conclude: "¿Quieres concluir realmente este leasing de coche?",
    },

    errors: {
      alreadyHasLeasing: "Para el coche especificado ya está definido un leasing.",
      expirationDateCantMatchContractDate: "La fecha de vencimiento no puede coincidir con la fecha del contrato",
      expirationDateCantBeBeforeContractDate: "La fecha de vencimiento no puede ser anterior a la fecha del contrato",
    },
  },

  mileages: {
    delete: "Eliminar kilometraje",
    edit: "Editar kilometraje",
    new: "Nuevo kilometraje",
    singular: "Kilometraje",
    plural: "Kilometrajes",
    versions: "Versiones del kilometraje",
    sendReminder: "Enviar recordatorio de kilometraje",
    sendReminders: "Enviar recordatorios",

    questions: {
      delete: "¿Quieres eliminar realmente este kilometraje?",
      sendReminder:
        "¿Quieres enviar al conductor actual un correo recordándole que introduzca el kilometraje actualizado de este vehículo?",
    },

    reminderDialog: {
      recipient: "Destinatario",
      lastReminder: "Último recordatorio de kilometraje enviado al conductor",
      noReminderSent: "Ningún recordatorio enviado",
      automaticReminderRule:
        "Fleet Manager envía automáticamente un recordatorio de kilometraje a los conductores de los vehículos sin kilometraje en los últimos 3 meses, pero omite a los conductores que ya recibieron uno en los últimos 3 días. Si actualizas la fecha del último recordatorio, este correo cuenta como el último recordatorio enviado y el conductor no recibirá el recordatorio automático durante los próximos 3 días, aunque en ese periodo venzan los 3 meses.",
      updateLastReminder: "Actualizar fecha del último recordatorio",
      driverNotificationsDisabled: (flag) =>
        `El correo no se puede enviar: el conductor tiene activada la opción "${flag}".`,
      driverNoEmail: "El correo no se puede enviar: el conductor no tiene dirección de correo.",
    },

    remindersDialog: {
      question:
        "¿Quieres enviar a los conductores actuales de los vehículos activos un correo recordándoles que introduzcan el kilometraje actualizado de su vehículo?",
      recipients: "Destinatarios",
      allDrivers: "Todos los conductores",
      onlyOverdue: "Solo kilometraje vencido (más de 3 meses)",
      allSubsidiaries: "Todas las filiales",
      summary: (drivers, emails) =>
        `Conductores a notificar: ${drivers} (correos a enviar: ${emails}, uno por vehículo).`,
      excluded: (excluded) =>
        `Conductores excluidos porque tienen las notificaciones desactivadas o no tienen dirección de correo: ${excluded}.`,
      noRecipients: "Ningún conductor a notificar.",
      onlyOverdueInfo: (option) =>
        `Con "${option}" solo se notifica a los conductores de los vehículos cuyo último kilometraje tiene más de 3 meses o que nunca han tenido un kilometraje.`,
      updateLastReminderInfo: (switchLabel) =>
        `Fleet Manager también envía un recordatorio automático a los conductores con kilometraje vencido, pero omite a quienes recibieron uno en los últimos 3 días. Al activar "${switchLabel}", la fecha del último recordatorio de los conductores notificados pasa a ser hoy: durante los próximos 3 días no recibirán el recordatorio automático, aunque en ese periodo venzan los 3 meses.`,
      backgroundInfo:
        "Los recordatorios se están enviando en segundo plano: puedes cerrar esta ventana y seguir trabajando. Recibirás un aviso al terminar el envío.",
      alreadySending: "Ya hay un envío de recordatorios de kilometraje en curso: podrás iniciar otro cuando termine.",
      sent: "Recordatorios de kilometraje enviados.",
    },

    notNotifiedDialog: {
      title: "Recordatorios de kilometraje no enviados",
      text: "No se ha podido enviar el recordatorio de kilometraje a los siguientes conductores:",
    },

    errors: {
      mileageWithThisDateAlreadyExists: "Para la fecha seleccionada, el mismo kilometraje ya ha sido registrado.",
      distanceLowerPrevRecord: "El kilometraje introducido es inferior al registro anterior.",
      distanceHigherNextRecord: "El kilometraje es superior al registro siguiente.",
      mileageOutOfSubsidiaryPeriod: "La fecha del kilometraje no está dentro del período de la filial.",
      mileageOutOfCarDriverPeriod:
        "La fecha del kilometraje está fuera del período en el que el vehículo te está asignado.",
      mileageMustBeBetween: (prevDate, prevDistance, nextDate, nextDistance) =>
        `Para la fecha seleccionada, la distancia debe estar entre ${prevDistance} (el ${prevDate}) y ${nextDistance} (el ${nextDate}).`,
      mileageMustBeGreater: (distance, date) =>
        `Para la fecha seleccionada, la distancia debe ser mayor que ${distance} (el ${date}).`,
      mileageMustBeLower: (distance, date) =>
        `Para la fecha seleccionada, la distancia debe ser menor que ${distance} (el ${date}).`,
      reminderMissingCurrentDriver: "El correo no se puede enviar: el vehículo no tiene un conductor actual.",
      reminderDriverMissingEmail: "El correo no se puede enviar: el conductor no tiene dirección de correo.",
      reminderDriverNotificationsDisabled:
        "El correo no se puede enviar: el conductor tiene las notificaciones desactivadas.",
      reminderSubsidiaryNotAllowed: "No tienes acceso a la filial seleccionada.",
    },
  },

  defaultTasks: {
    new: "Nueva actividad predeterminada",
    edit: "Editar actividad predeterminada",
    delete: "Eliminar actividad predeterminada",
    singular: "Actividad predeterminada",
    plural: "Actividades predeterminadas",

    questions: {
      delete: "¿Quieres eliminar realmente esta actividad predeterminada?",
    },
  },

  customTasks: {
    set: "Configurar actividades personalizadas",
    create: "Crear actividades personalizadas",
    edit: "Editar actividades personalizadas",
    singular: "Actividad personalizada",
    plural: "Actividades personalizadas",

    questions: {
      customize: "La filial utiliza actualmente las actividades predeterminadas. ¿Quieres personalizarlas?",
    },

    errors: {
      taskAlreadySelected: "La actividad ya ha sido seleccionada.",
    },
  },

  tasks: {
    delete: "Eliminar actividad",
    rename: "Renombrar actividad",
    edit: "Editar actividad",
    new: "Nueva actividad",
    singular: "Actividad",
    plural: "Actividades",
    versions: "Versiones de la actividad",

    questions: {
      delete: "¿Quieres eliminar realmente esta actividad?",
    },

    errors: {
      taskNameAlreadyExists: "Ya existe una actividad predeterminada con el mismo nombre.",
      inUseByCustomTasks:
        "La actividad predeterminada todavía está utilizada por una actividad personalizada y no puede eliminarse.",
    },
  },

  tasksCategories: {
    delete: "Eliminar categoría de actividades",
    plural: "Categorías de actividades",

    questions: {
      deleteSubsidiaryCustomTasks:
        "¿Quieres eliminar realmente esta categoría personalizada y asociar las actividades predeterminadas?",
    },
  },

  carTasks: {
    delete: "Eliminar actividad del coche",
    edit: "Editar actividad del coche",
    new: "Nueva actividad del coche",
    singular: "Actividad del coche",
    plural: "Actividades del coche",

    questions: {
      delete: "¿Quieres eliminar realmente esta actividad?",
      deleteWithWorkflow:
        "Esta tarea está asociada a un workflow. Si continúa, tanto la tarea como el workflow relacionado serán eliminados. ¿Desea continuar?",
    },

    errors: {
      inUseByWorkflows:
        "El task del vehículo todavía está siendo utilizada por un flujo de trabajo y no se puede eliminar.",
    },
  },

  leasingCompanies: {
    delete: "Eliminar empresa de leasing",
    edit: "Editar empresa de leasing",
    new: "Nueva empresa de leasing",
    singular: "Empresa de leasing",
    plural: "Empresas de leasing",
    versions: "Versiones de la empresa de leasing",

    questions: {
      delete: "¿Quieres eliminar realmente esta empresa de leasing?",
    },

    errors: {
      nameAlreadyExists: "Ya existe una empresa de leasing con el mismo nombre.",
      inUseByCarLeasings: "La empresa de leasing todavía está utilizada por un leasing de coche y no puede eliminarse.",
    },
  },

  plateNumbers: {
    singular: "Matrícula",
  },

  highwayPasses: {
    delete: "Eliminar telepeaje",
    edit: "Editar telepeaje",
    new: "Nuevo telepeaje",
    singular: "Telepeaje",
    plural: "Telepeajes",
    versions: "Versiones del telepeaje",

    questions: {
      delete: "¿Quieres eliminar realmente este telepeaje?",
    },

    errors: {
      highwayPassOverlaps:
        "Un telepeaje con el mismo número y para el período especificado ya está utilizado por otro coche.",
      highwayPassOutOfSubsidiaryPeriod: "El período del telepeaje no está dentro del período de la filial.",
    },
  },

  costTypeCategories: {
    delete: "Eliminar categoría de tipo de coste",
    edit: "Editar categoría de tipo de coste",
    new: "Nueva categoría de tipo de coste",
    singular: "Categoría de tipo de coste",
    plural: "Categorías de tipo de coste",
    versions: "Versiones de la categoría de tipo de coste",

    questions: {
      delete: "¿Quieres eliminar realmente esta categoría de tipo de coste?",
    },

    errors: {
      isUsedByCostType:
        "La categoría de tipo de coste todavía está utilizada por un tipo de coste y no puede eliminarse.",
      nameAlreadyUsed: "Ya está definida una categoría de tipo de coste con el mismo nombre.",
    },
  },

  costTypes: {
    delete: "Eliminar tipo de coste",
    edit: "Editar tipo de coste",
    new: "Nuevo tipo de coste",
    singular: "Tipo de coste",
    plural: "Tipos de coste",
    versions: "Versiones del tipo de coste",

    questions: {
      delete: "¿Quieres eliminar realmente este tipo de coste?",
    },

    errors: {
      isUsedByCosts: "El tipo de coste todavía está utilizado por un coste y no puede eliminarse.",
      nameAlreadyUsed: "Ya existe un tipo de coste con este nombre.",
    },
  },

  costs: {
    delete: "Eliminar coste",
    edit: "Editar coste",
    new: "Nuevo coste",
    singular: "Coste",
    plural: "Costes",
    versions: "Versiones del coste",

    questions: {
      delete: "¿Quieres eliminar realmente este coste?",
    },

    errors: {
      costOutOfSubsidiaryPeriod: "La fecha del coste no está dentro del período de la filial.",
      missingCurrency: (currency: string) => `No se puede convertir el coste a EUR: moneda ${currency} no encontrada.`,
    },
  },

  maintenanceTypes: {
    delete: "Eliminar tipo de mantenimiento",
    edit: "Editar tipo de mantenimiento",
    new: "Nuevo tipo de mantenimiento",
    singular: "Tipo de mantenimiento",
    plural: "Tipos de mantenimiento",

    questions: {
      delete: "¿Quieres eliminar realmente este tipo de mantenimiento?",
    },

    errors: {
      isUsedByMaintenance:
        "El tipo de mantenimiento todavía está utilizado por un mantenimiento y no puede eliminarse.",
      nameAlreadyUsed: "Ya existe un tipo de mantenimiento con el mismo nombre.",
    },
  },

  maintenance: {
    delete: "Eliminar mantenimiento",
    edit: "Editar mantenimiento",
    new: "Nuevo mantenimiento",
    singular: "Mantenimiento",
    plural: "Mantenimientos",

    questions: {
      delete: "¿Quieres eliminar realmente este mantenimiento?",
    },
  },

  costsImport: {
    delete: "Eliminar importación de costes",
    changeCarAssignment: "Cambiar vehículo asociado",
    changeCarAssignmentDisclaimer: (plate: string, date: string) =>
      `Este coste se facturó a la matrícula ${plate} el ${date}. Antes de realizar un cambio manual, verifique los datos actuales en Fleet Management para asegurarse de que esta corrección sea realmente necesaria.`,
    noDriverCoversDate: "Ninguna de las asignaciones indicadas arriba cubre la fecha de este coste.",
    missingAssignmentCostCenter: (name: string) =>
      `${name} cubre la fecha de este coste pero no tiene un centro de coste definido.`,
    carForcedManually: (date: string) => `El vehículo se asignó manualmente el ${date}.`,
    assignmentForcedManually: (date: string) => `La asignación del conductor se forzó manualmente el ${date}.`,
    carForcedFromTo: (fromPlate: string, toPlate: string, name: string, date: string) =>
      `Esta asignación de vehículo se cambió de ${fromPlate} a ${toPlate} por ${name} el ${date}.`,
    removeForcedCar: "Quitar forzado",
    costWillBeAssignedTo: (name: string) => `Este coste se asignará a ${name}.`,
    forceThisDriver: "Forzar este conductor",
    removeAssignment: "Quitar asignación",
    forcedAssignmentDoesNotCoverDate: "Esta asignación no cubre la fecha de este coste.",
    excludedFromExport: (plate: string) =>
      `Para ${plate} esta línea no se exporta a costes: solo se exportan los vehículos pkw, pkw-superior y lkw.`,

    questions: {
      deleteImport: "¿Quieres eliminar realmente esta importación de costes?",
      importInCosts: "¿Quieres importar realmente estas filas en los costes?",
      forceDriver: (name: string) =>
        `¿Quieres forzar realmente que este coste se asigne a ${name}? Esto anula la asignación automática basada en la fecha.`,
    },

    dkv: {
      noCarFound: (plate: string, date: string) =>
        `Para la matrícula ${plate} ningún vehículo cubre el coste del ${date}`,
      noDriverFound: (plate: string, date: string) =>
        `Para el vehículo ${plate} ninguna asignación cubre el coste del ${date}`,
      noSubsidiaryVatCodeFound: (productCode: string) => `Para ${productCode} no se encontró ningún código IVA`,
      noCostTypeFound: (productCode: string, category?: string) =>
        `Para ${productCode}${category ? `/${category}` : ""} no se encontró ningún tipo de coste`,
      noCostCenterFound: (name: string, date: string) =>
        `Para ${name} ningún centro de costes cubre el coste del ${date}`,
      noUserCostCenterFound: (plate: string, date: string) =>
        `Para el vehículo ${plate} ningún centro de costes de usuario cubre el coste del ${date}`,

      errors: {
        columnTargaVeicolo: "Columna 'Targa veicolo' no encontrada",
        columnDataFattura: "Columna 'Data della fattura' no encontrada",
        columnOraTransazione: "Columna 'Ora transazione' no encontrada",
        columnValoreBaseNetto: "Columna 'Valore base netto' no encontrada",
        columnValoreBaseLordo: "Columna 'Valore base lordo' no encontrada",
        columnCodiceMerce: "Columna 'Codice merce' no encontrada",
        columnGruppoProdotti: "Columna 'Gruppo di prodotti' no encontrada",
        columnTipoMerce: "Columna 'Tipo di merce' no encontrada",
        columnPaesePedaggio: "Columna 'Paese di pedaggio' no encontrada",
        columnIva: "Columna 'IVA' no encontrada",
        columnScontoNetto: "Columna 'Sconto netto' no encontrada",
        columnScontoLordo: "Columna 'Sconto lordo' no encontrada",
        columnServiceFeeNetto: "Columna 'Service fee netto' no encontrada",
        columnQuantita: "Columna 'Quantità' no encontrada",
        columnUnita: "Columna 'Unità' no encontrada",
      },
    },

    dkvTolls: {
      errors: {
        columnDataFattura: "Columna 'Data della fattura' no encontrada",
        columnTargaVeicolo: "Columna 'Targa veicolo' no encontrada",
        columnOraUscita: "Columna 'Ora uscita' no encontrada",
        columnCodiceMerce: "Columna 'Codice merce' no encontrada",
        columnValutaServizio: "Columna 'Valuta del servizio' no encontrada",
        columnIva: "Columna 'IVA' no encontrada",
        columnValoreBaseNetto: "Columna 'Valore base netto' no encontrada",
        columnPaesePedaggio: "Columna 'Paese di pedaggio' no encontrada",
      },
    },
  },

  dueDateTypes: {
    delete: "Eliminar tipo de vencimiento",
    edit: "Editar tipo de vencimiento",
    new: "Nuevo tipo de vencimiento",
    singular: "Tipo de vencimiento",
    plural: "Tipos de vencimiento",
    versions: "Versiones del tipo de vencimiento",

    questions: {
      delete: "¿Quieres eliminar realmente este tipo de vencimiento?",
    },

    errors: {
      isUsedByDueDates: "El tipo de vencimiento todavía está utilizado por un vencimiento y no puede eliminarse.",
      nameAlreadyUsed: "Ya existe un tipo de vencimiento con el mismo nombre.",
    },
  },

  dueDates: {
    all: "Todos los vencimientos",
    delete: "Eliminar vencimiento",
    edit: "Editar vencimiento",
    new: "Nuevo vencimiento",
    singular: "Vencimiento",
    plural: "Vencimientos",
    versions: "Versiones del vencimiento",
    renewPlural: "Renovar vencimientos",
    renewSingular: "Renovar vencimiento",
    renewSelected: "Renovar vencimientos seleccionados",
    setSettledSelected: "Marcar como saldados los vencimientos seleccionados",

    questions: {
      delete: "¿Quieres eliminar realmente este vencimiento?",
      setSettled: "¿Quieres marcar como saldados los vencimientos seleccionados?",
    },

    errors: {
      dueDateOutOfSubsidiaryPeriod: "La fecha del vencimiento no está dentro del período de la filial.",
    },
  },

  documents: {
    add: "Añadir documento",
    all: "Todos los documentos",
    alreadyPresent: "Documento ya presente.",
    plural: "Documentos",
    delete: "Eliminar documento",
    search: "Buscar documentos",

    questions: {
      delete: "¿Quieres eliminar realmente este documento?",
      overwrite: "¿Quieres sobrescribir el documento ya presente?",
    },

    errors: {
      notFound: "El documento no fue encontrado",
    },
  },

  users: {
    all: "Todos los usuarios",
    delete: "Eliminar usuario",
    edit: "Editar usuario",
    new: "Nuevo usuario",
    singular: "Usuario",
    plural: "Usuarios",

    questions: {
      delete: "¿Quieres eliminar realmente este usuario?",
    },

    errors: {
      noMatchingUserFound: "No se encontró ningún usuario coincidente.",
      rothoblaasUserWithEmailNotFound:
        "No se encontró ningún usuario de Rothoblaas con el correo electrónico especificado.",
      usersWithEmailAlreadyExists: "Ya existe un usuario con el email especificado.",
      inUseByCarTasks: "El usuario todavía está utilizado por una actividad del coche y no puede eliminarse.",
      inUseByCarOrders: "El usuario todavía está utilizado por una compra de coche y no puede eliminarse.",
      inUseByCarDrivers: "El usuario todavía está utilizado como conductor y no puede eliminarse.",
      usersWithEmailNotFound: "No se encontró ningún usuario con la dirección de correo electrónico especificada.",
      invalidEmailDomain: "El dominio del correo electrónico no es válido para usuarios de Rothoblaas.",
      userSubsidiaryAlreadyExists: "El usuario ya está asignado a la filial especificada.",
      userInUseAsManager: "El usuario todavía está utilizado como manager y no puede eliminarse.",
    },
  },

  claims: {
    all: "Todos los siniestros",
    delete: "Eliminar siniestro",
    edit: "Editar siniestro",
    new: "Nuevo siniestro",
    singular: "Siniestro",
    plural: "Siniestros",
    incidentDate: "Fecha del siniestro",
    openingDate: "Fecha de apertura",
    closingDate: "Fecha de cierre",
    damageValue: "Valor del daño",
    settledAmount: "Importe liquidado",

    questions: {
      delete: "¿Quieres eliminar realmente este siniestro?",
      switchDriver: (selectedDriver: string, currentDriver: string) =>
        `El conductor seleccionado es ${selectedDriver}, pero el asignado actualmente es ${currentDriver}.<br /> ¿Desea asignar esta reclamación a ${currentDriver}?`,
      switchSubsidiary: (selectedSubsidiary: string, currentSubsidiary: string) =>
        `La filial seleccionada es ${selectedSubsidiary}, pero la asignada actualmente es ${currentSubsidiary}.<br /> ¿Desea asignar este siniestro a ${currentSubsidiary}?`,
    },
  },

  fines: {
    delete: "Eliminar multa",
    edit: "Editar multa",
    new: "Nueva multa",
    singular: "Multa",
    plural: "Multas",
    verbalNumber: "Número de boletín",
    violationDate: "Fecha de la infracción",
    communicationDate: "Fecha de comunicación",
    firstNotificationDate: "Fecha de primera notificación",
    amount: "Importe",
    points: "Puntos",
    accountant: "Contable",
    hrReferent: "Referente de RR.HH.",
    notifyDriver: "Notificar al conductor",
    archive: "Archivar",
    notifications: "Notificaciones",
    sentBy: "Enviado por",
    sentAt: "Enviado el",
    notifyDialog: {
      recipient: "Destinatario",
      attachments: "Adjuntos",
      noAttachments: "Selecciona al menos un documento para enviar.",
      addDocuments: "Añadir documentos",
      dropDocumentsHint: "También puedes arrastrar los archivos a la tabla",
      attachmentsHint: "Los documentos añadidos aquí también se guardan en los documentos de la multa.",
      alreadyNotified: (name: string, date: string) => `Ya notificada por ${name} el ${date}.`,
      driverNotificationsDisabled: (flag: string) =>
        `El correo no se puede enviar: el conductor tiene activada la opción "${flag}".`,
      driverNoEmail: "El correo no se puede enviar: el conductor no tiene dirección de correo.",
      userNotificationsDisabled: (flag: string) =>
        `Este usuario tiene activada la opción "${flag}" y no puede recibir la copia.`,
      userNoEmail: "Este usuario no tiene dirección de correo y no puede recibir la copia.",
    },
    errors: {
      notifyArchived: "Una multa archivada no se puede notificar.",
      alreadyArchived: "La multa ya está archivada.",
      driverMissingEmail: "El correo no se puede enviar: el conductor no tiene dirección de correo.",
      driverNotificationsDisabled: "El correo no se puede enviar: el conductor tiene las notificaciones desactivadas.",
    },
    statuses: {
      new: "Nueva",
      notified: "Notificada",
      archived: "Archivada",
    },
    questions: {
      delete: "¿Quieres eliminar realmente esta multa?",
      archive: "¿Quieres archivar realmente esta multa?",
    },
  },

  claimTypes: {
    all: "Todos los tipos de siniestro",
    delete: "Eliminar tipo de siniestro",
    edit: "Editar tipo de siniestro",
    new: "Nuevo tipo de siniestro",
    singular: "Tipo de siniestro",
    plural: "Tipos de siniestro",

    questions: {
      delete: "¿Quieres eliminar realmente este tipo de siniestro?",
    },

    errors: {
      nameAlreadyUsed: "Ya existe un tipo de siniestro con este nombre.",
      inUseByClaims: "El tipo de reclamación todavía está en uso en los siniestros y no se puede eliminar.",
    },
  },

  clients: {
    all: "Todos los clients",
    plural: "Clients",
    createNew: "Crear nuevo client",
    delete: "Eliminar client",
    search: "Buscar clients",

    questions: {
      delete: "¿Quieres eliminar realmente este client?",
    },

    errors: {
      notFound: "Client no encontrado.",
      missingName: "Falta el nombre del client.",
      insertNotAllowed: "No estás autorizado a insertar un nuevo client.",
      readNotAllowed: "No estás autorizado a leer este client.",
      updateNotAllowed: "No estás autorizado a actualizar este client.",
      deleteNotAllowed: "No estás autorizado a eliminar este client.",
    },
  },

  subsidiaryVatCodes: {
    delete: "Eliminar código de IVA de la filial",
    edit: "Editar código de IVA de la filial",
    new: "Nuevo código de IVA de la filial",
    singular: "Código de IVA de la filial",
    plural: "Códigos de IVA de la filial",
    versions: "Versiones del código de IVA de la filial",

    questions: {
      delete: "¿Quieres eliminar realmente este código de IVA de la filial?",
    },
  },

  roles: {
    delete: "Eliminar rol",
    edit: "Editar rol",
    new: "Nuevo rol",
    singular: "Rol",
    plural: "Roles",

    questions: {
      delete: "¿Quieres eliminar realmente este rol?",
    },
  },

  permissions: {
    plural: "Permisos",
  },

  secrets: {
    plural: "Secretos",

    errors: {
      missingName: "Falta el nombre del secreto.",
      missingValue: "Falta el valor del secreto.",
    },
  },

  carPoolCostCenters: {
    delete: "Eliminar centro de coste del parque de coches",
    edit: "Editar centro de coste del parque de coches",
    new: "Nuevo centro de coste del parque de coches",
    singular: "Centro de coste del parque de coches",
    plural: "Centros de coste del parque de coches",

    questions: {
      delete: "¿Quieres eliminar realmente este centro de coste del parque de coches?",
    },

    errors: {
      carPoolOverlapPeriod: "El parque de coches ya tiene un centro de coste en este período",
    },
  },

  otherCarAssignments: {
    delete: "Eliminar otra asignación de coche",
    edit: "Editar otra asignación de coche",
    new: "Nueva otra asignación de coche",
    singular: "Otra asignación de coche",
    plural: "Otras asignaciones de coche",

    questions: {
      delete: "¿Quieres eliminar realmente esta otra asignación de coche?",
    },

    errors: {
      carPoolOverlapPeriod: "El coche ya está en el parque de coches en este período",
      carPoolOverlapPeriodWithDrivers: "El coche ya tiene un conductor en este período",
      assignmentOutOfSubsidiaryPeriod: "El período de la asignación no está dentro del período de la filial.",
    },
  },

  carPlannings: {
    delete: "Eliminar planificación del vehículo",
    edit: "Editar planificación del vehículo",
    new: "Nueva planificación del vehículo",
    singular: "Planificación del vehículo",
    plural: "Planificaciones del vehículo",
    versions: "Versiones de la planificación del vehículo",

    questions: {
      delete: "Desea realmente eliminar esta planificación del vehículo?",
    },

    errors: {
      missingSemiramisCode:
        "Filial sin código de organización de Semiramis. Por favor, configure el código de Semiramis en la filial seleccionada",
    },
  },

  carSubsidiaries: {
    delete: "Eliminar filial del coche",
    edit: "Editar filial del coche",
    new: "Nueva filial del coche",
    singular: "Filial del coche",
    plural: "Filiales del coche",
    versions: "Versiones de la filial del coche",

    questions: {
      delete: "¿Quieres eliminar realmente esta filial del coche?",
    },

    errors: {
      overlapPeriodCarSubsidiary: "El período especificado se superpone con otra filial.",
      overlapPeriodCarPlate: "Hay matrículas de coche que se superponen con el período de otras filiales.",
      overlapPeriodCarDriver: "Hay conductores de coche que se superponen con el período de otras filiales.",
    },
  },

  rentalCars: {
    delete: "Eliminar coche de alquiler",
    edit: "Editar coche de alquiler",
    new: "Nuevo coche de alquiler",
    singular: "Coche de alquiler",
    plural: "Coches de alquiler",

    questions: {
      delete: "¿Realmente quieres eliminar este coche de alquiler?",
    },

    errors: {
      overlapPeriodCar: "El período especificado se superpone con otro coche de alquiler.",
    },
  },

  userSubsidiaries: {
    delete: "Eliminar sucursal de usuario",
    edit: "Editar sucursal de usuario",
    new: "Nueva sucursal de usuario",
    singular: "Sucursal de usuario",
    plural: "Sucursales de usuario",
    versions: "Versiones de la sucursal de usuario",

    questions: {
      delete: "¿Desea realmente eliminar esta sucursal de usuario?",
    },
  },

  userRoles: {
    delete: "Eliminar rol de usuario",
    edit: "Editar rol de usuario",
    new: "Nuevo rol de usuario",
    singular: "Rol de usuario",
    plural: "Roles de usuario",
    versions: "Versiones del rol de usuario",

    questions: {
      delete: "¿Desea realmente eliminar este rol de usuario?",
    },

    errors: {
      alreadyExists: "El rol de usuario ya existe",
    },
  },

  common: {
    acceptImages: "Aceptar imágenes",
    acceptImagesDialog: "Las imágenes cargadas serán aceptadas, y el conductor será notificado por correo electrónico.",
    accessNotAllowed: "Acceso no permitido",
    accessNotAllowedError: "No tienes permisos para ver esta página.",
    active: "Activo",
    action: "Acción",
    accounting: "Contabilidad",
    add: "Añadir",
    addCustomTask: "Agregar task personalizado",
    addDefaultTask: "Agregar task predeterminado",
    addCustomCarTask: "Agregar task de coche personalizado",
    addDefaultCarTask: "Agregar task de coche predeterminado",
    addDueDate: "Añadir vencimiento",
    addNew: "Añadir nuevo",
    addNewAssignment: "Añadir nueva asignación",
    addMileage: "Añadir kilometraje",
    address: "Dirección",
    addTask: "Añadir actividad",
    addToCarPool: "Añadir al parque de coches",
    addToHistory: "Añadir al historial",
    admin: "Admin",
    administration: "Administración",
    agreement: "Acuerdo",
    all: "Todos",
    allowCarsToHaveMultiplePlates: "Permitir que los coches tengan múltiples matrículas",
    allowMultiplePlates: "Permitir múltiples matrículas",
    alreadyInCarPool: "Coche ya en el parque de coches",
    alreadyPresent: "Ya presente",
    alreadyUpdated:
      "Este registro ha sido actualizado por otro usuario. Actualiza la página para obtener la versión más reciente.",
    areaSalesManager: "Responsable de ventas de área",
    arrived: "Llegada",
    arrivingSoon: "Llegará pronto",
    ascending: "Ascendente",
    assign: "Asignar",
    assignCar: "Asignar coche",
    assignHp: "Asignar nuevo telepeaje",
    assignNewDriver: "Asignar nuevo conductor",
    assignNewPlate: "Asignar nueva matrícula",
    assigned: "Asignado",
    assignedTo: "Asignado a",
    assignSubsidiary: "Asignar filial",
    attachments: "Archivos adjuntos",
    attachmentsHelpDialog:
      "Es posible adjuntar imágenes y/o documentos; los archivos cargados serán visibles para usted y para los administradores del sistema Fleet Manager.",
    avgYears: "Ø Años",
    awaitingForApproval: "Pendiente de aprobación",
    back: "Atrás",
    birthdate: "Fecha de nacimiento",
    business: "Negocio",
    by: "De",
    cancel: "Cancelar",
    cancelled: "Cancelado",
    cancelWorkflow: "Cancelar workflow",
    cancelWorkflowRequest:
      "El flujo de trabajo se detendrá. Todas las cargas de imágenes pendientes se cancelarán. ¿Desea continuar?",
    car: "Coche",
    carAssignment: "Asignación de vehículo",
    carCategory: "Categoría de coche",
    carInCarPool: "Coche en el parque de coches",
    carNotInUse: "Coche no en uso",
    carPool: "Parque de coches",
    carPoolCostCenters: "Centros de coste del parque de coches",
    carOrdersWithNearTermText: {
      prefix: "Hay algunas compras de coches con fecha de llegada cercana que aún no han sido asignadas. Haz clic ",
      linkTitle: "aquí",
      suffix: " para listarlas y gestionarlas.",
    },
    carReplacedRental: "Coche de sustitución de alquiler",
    cartax: "Impuesto del coche",
    carVin: "Coche (Bastidor)",
    categories: "Categorías",
    category: "Categoría",
    changeFlag: "Cambiar bandera",
    checkWorkflow: "Revisar workflow",
    clear: "Borrar",
    close: "Cerrar",
    closed: "Cerrado",
    awaitingDocuments: "Pendiente de documentos",
    closingDate: "Fecha de cierre",
    columnNotFilterable: "Esta columna no se puede filtrar",
    filterEquals: "Igual a",
    filterRange: "Desde - Hasta",
    filterMonth: "Mes",
    columnNotSortable: "Esta columna no se puede ordenar",
    continue: "Continuar",
    contractDate: "Fecha del contrato",
    contractDetails: "Detalles del contrato",
    cost: "Coste",
    costInputFormNet: "Coste (Neto - Moneda)",
    countDate: "Fecha de recuento",
    country: "País",
    countryIsoCode: "Código ISO del país",
    countryIsoCodeRequiredForAgreement:
      "La creación de un acuerdo requiere que el código ISO del país de la filial esté configurado.",
    costCenter: "Centro de coste",
    costCenterHistory: "Historial del centro de coste",
    costObject: "Objeto de coste",
    costImport: "Importación de costes",
    costImports: "Importaciones de costes",
    costGross: "Coste bruto",
    costNet: "Coste neto",
    co2: "CO2",
    create: "Crear",
    createdAt: "Creado el",
    createdBy: "Creado por",
    createDelegation: "Crear delegación",
    createDelegationWithDriverSelection: "Crear delegación con selección de conductor",
    createDelegationForExternal: "Crear delegación para externos",
    createNew: "Crear nuevo",
    currency: "Moneda",
    currentHighwayPass: "Telepeaje actual",
    currentDriver: "Conductor actual",
    currentlyInCarPool: "Este coche está actualmente en el parque de coches.",
    currentlyNotUsed: "Este coche no está actualmente en uso.",
    currentlyAssignToDriver: "Este coche está actualmente asignado a otro conductor.",
    currentPlateNumber: "Matrícula actual",
    currentSubsidiary: "Filial actual",
    currentVersion: "Versión actual",
    dashboard: "Panel",
    dataConflicts: (value: string) => `Conflictos de datos en la fecha ${value}`,
    dataHasBeenSavedSuccessfully: "Datos guardados correctamente.",
    dataHasBeenUpdatedSuccesfully: "Datos actualizados correctamente.",
    date: "Fecha",
    datePicker: {
      clearButton: "Borrar fecha",
      nextMonth: "Mes siguiente",
      placeholder: "Seleccionar fecha",
      previousMonth: "Mes anterior",
      selectMonth: "Seleccionar mes",
      selectYear: "Seleccionar año",
    },
    decrease: (value: number) => `Disminuir ${value}`,
    defaultCurrency: "Moneda predeterminada",
    defaultLanguage: "Idioma predeterminado",
    delegation: "Delegación",
    delete: "Eliminar",
    deleteFilter: (filter: string) => `Eliminar filtros para ${filter}`,
    deleteFilters: "Eliminar filtros",
    department: "Departamento",
    departments: "Departamentos",
    descending: "Descendente",
    description: "Descripción",
    diesel: "Diésel",
    discountGross: "Descuento bruto",
    discountNet: "Descuento neto",
    distance: "Distancia",
    distances: "Distancias",
    distanceInMiles: "Distancia en millas",
    divisionSalesManager: "Responsable de ventas de división",
    dkvProductCodes: "Códigos de producto DKV",
    documents: "Documentos",
    done: "Hecho",
    download: "Descargar",
    downloadFile: "Descargar archivo",
    dragAndDropOrUploadAnImage: "Arrastra y suelta o haz clic para subir una imagen",
    driverAlreadyNotified: "Ya se ha enviado un correo electrónico de notificación a este usuario. ¿Desea reenviarlo?",
    driverNotified: (on: string) => `Conductor notificado el ${on}. Esperando a que se suban las imágenes.`,
    dragAndDropOrUploadAnFile: "Arrastra y suelta o haz clic para subir un archivo",
    driverName: "Nombre del conductor",
    driverSince: "Conductor desde",
    drivingLicenseExpirationDate: "Fecha de caducidad del permiso de conducir",
    drivingLicenseText:
      "Al introducir en el sistema la fecha de vencimiento de tu licencia de conducir, recibirás automáticamente una notificación de recordatorio aproximadamente 20 días antes de su vencimiento. La información introducida no es rastreada ni gestionada por Fleet Management, sino que es procesada exclusivamente por el sistema.",
    dropIt: "Soltar",
    dueDateHelpDialog: "Al introducir una fecha, el vencimiento se actualizará con la fecha seleccionada.",
    dueDateVisibleToUser:
      "La selección de un usuario hace que la fecha de vencimiento sea visible tanto para el propio usuario como para los gerentes asociados.",
    edit: "Editar",
    editedBy: "Editado por",
    editNotPermitted: "La edición ya no está permitida — ha pasado más de un día desde la creación.",
    electric: "Eléctrico",
    email: "Email",
    emailSendToDriver: "Se enviará una notificación por email al conductor.",
    empty: "Vacío",
    endDate: "Fecha de fin",
    engineCapacity: "Cilindrada",
    english: "Inglés",
    englishUk: "Inglés - Reino Unido",
    englishUs: "Inglés - EE. UU.",
    englishBrasil: "Inglés - Brasil",
    enteredDate: "Fecha de entrada",
    enterAPhoneNumber: "Introduce un número de teléfono",
    enterValidNames: "Introduce nombres válidos.",
    error: "Error",
    errorLoadingData: "Error al cargar los datos",
    expirationDate: "Fecha de caducidad",
    expired: "Caducado",
    expiringSoon: "A punto de caducar",
    exportExcel: "Exportar Excel",
    fieldCannotBeEmpty: "El campo no puede estar vacío.",
    file: "Archivo",
    fileCsv: "Archivo CSV",
    fileNotSupported: `El archivo no es compatible.`,
    fileTypeSupported: (extensions: string[]) => `Tipos de archivo compatibles: ${extensions.join(", ")}.`,
    fileToLarge: "Archivo demasiado grande",
    fiscalPrice: "Precio fiscal",
    fieldContainsInvalidValueInsertBiggerOne: "El campo contiene un valor no válido, introduce uno mayor.",
    fieldContainsInvalidValueInsertSmallerOne: "El campo contiene un valor no válido, introduce uno menor.",
    fieldInvalidValue: "El campo contiene un valor no válido.",
    fieldValueInvalidFormat: "El valor tiene un formato no válido.",
    firstName: "Nombre",
    flag: "Bandera",
    french: "Francés",
    fringeBenefit: "Beneficio en especie",
    from: "De",
    fuelCard: "Tarjeta de combustible",
    fuelType: "Tipo de combustible",
    gasoline: "Gasolina",
    generalData: "Datos generales",
    generateAgreement: "Generar acuerdo",
    genericError: "Se ha producido un error, inténtalo de nuevo.",
    german: "Alemán",
    gpl: "GLP",
    hasImportErrors: "Contiene errores de importación",
    hidden: "Oculto",
    hiddenText:
      "Dado que los usuarios se importan regularmente desde el sistema Rothoblaas, algunas cuentas podrían no ser necesarias para el sistema de gestión. Este indicador permite ocultar un usuario de la gestión de conductores.",
    disableNotifications: "Desactivar notificaciones",
    disableNotificationsText:
      "Si este indicador esta activo, no se enviaran los correos electronicos de notificacion automatica. Los demas correos generados por acciones del usuario se seguiran enviando.",
    hideArrivalNotification: "Ocultar notificación de fecha de llegada",
    hideToUser: "Ocultar para el usuario",
    history: "Historial",
    hp: "CV",
    hybrid: "Híbrido",
    imagesAccepted: "Imágenes aceptadas",
    imagesDeleted: "Imágenes eliminadas",
    imagesSupplied: "Imágenes proporcionadas",
    imperial: "Imperial (millas)",
    importInCosts: "Importar en costes",
    import: "Importar",
    importCsv: "Importar CSV",
    importErrors: "Errores de importación",
    importErrorsText:
      "Los centros de coste se importan automáticamente desde bValue (Provisius).<br/>Las filas siguientes indican los registros que no se han podido importar.<br/>Verifica las fechas con RR. HH. y corrígelas abajo.",
    importDkv: "Importar DKV",
    importHighwayToll: "Importar peajes de autopista",
    importedInCosts: "Importado en costes",
    increase: (value: number) => `Aumentar ${value}`,
    insert: "Insertar",
    insurance: "Seguro",
    invalidDate: "Fecha no válida",
    invalidLenght: "Longitud no válida",
    invalidNumber: "Número no válido",
    isInPool: "Está en el parque de coches",
    isRothoblaasUser: "Es usuario Rothoblaas",
    isRothoblaasUserTexts: [
      "Este indicador se utiliza para distinguir a los usuarios de Rothoblaas de los usuarios externos. Cuando se activa, el sistema verifica que la dirección de correo electrónico sea válida. En ese caso, el nombre y el apellido se obtienen de la base de datos de Rothoblaas. Además, no es posible guardar un usuario con una dirección de correo electrónico de Rothoblaas si no existe una coincidencia con usuarios activos.",
      "Con el indicador desactivado, la dirección de correo electrónico sigue siendo opcional y no se realizan comprobaciones sobre los usuarios de Rothoblaas. En este caso, el nombre y el apellido son obligatorios.",
    ],
    isSettled: "Está saldado",
    italian: "Italiano",
    portuguese: "Portugués",
    withErrors: "Con errores",
    withoutErrors: "Sin errores",
    itemsNotExported: "No exportados",
    itemsForced: "Forzados",
    forcedBy: "Forzado por",
    keep: "Mantener",
    kilometers: "Kilómetros",
    language: "Idioma",
    languageCode: "Código de idioma",
    lastDriver: "Último conductor",
    lastEdit: "Última edición de",
    lastHighwayPass: "Último telepeaje",
    lastMileage: "Último kilometraje",
    lastMileageRec: "Último registro de kilometraje",
    lastName: "Apellido",
    lastPlate: "Última matrícula",
    lastRecord: "Último registro",
    lastUpdate: "Última actualización",
    lastUpdatedBy: "Última actualización por",
    latvian: "Letón",
    leasing: "Leasing",
    lessThanDueDateRecord: "es inferior a la fecha de uno de los registros seleccionados",
    licensePlateValidityEnd: "Fin de validez de la matrícula",
    licensePlateValidityStart: "Inicio de validez de la matrícula",
    lithuanian: "Lituano",
    loading: "Cargando",
    logout: "Cerrar sesión",
    lkw: "Lkw",
    lkwAccountingNumber: "Número contable Lkw",
    lkwAccountingNumberShort: "Lkw Num. cont.",
    manageCategories: "Gestionar categorías",
    manageClaimTypes: "Gestionar tipos de siniestro",
    manageCostTypeCategories: "Gestionar categorías de tipos de coste",
    manageCostTypes: "Gestionar tipos de coste",
    vatCodes: "Códigos IVA",
    manageDefaultTasks: "Gestionar actividades predeterminadas",
    manageDueDateTypes: "Gestionar tipos de vencimiento",
    manageMaintenanceTypes: "Gestionar tipos de mantenimiento",
    manager: "Responsable",
    managerEmail: "Email del responsable",
    managerName: "Nombre del responsable",
    metric: "Métrico (km)",
    mileage: "Kilometraje",
    mileages: "Kilometrajes",
    miles: "millas",
    missingContractDate: "Falta la fecha del contrato",
    missingCountry: "Falta el país",
    missingExpirationDate: "Falta la fecha de caducidad",
    missingLeasingCompany: "Falta la empresa de leasing",
    missingRenewalDate:
      "¡Atención! No se ha introducido ninguna fecha de renovación; el vencimiento no se actualizará y simplemente se cerrará.",
    modifiedAt: "Modificado el",
    modifiedBy: "Modificado por",
    model: "Modelo",
    monthYearPicker: {
      clearButton: "Borrar mes",
      nextYear: "Año siguiente",
      placeholder: "Seleccionar mes",
      previousYear: "Año anterior",
      selectYear: "Seleccionar año",
    },
    myArea: "Mi área",
    myCar: "Mi coche",
    myCars: "Mis coches",
    myProfile: "Mi perfil",
    myTeamCar: "Coche de mi equipo",
    myTeamCars: "Coches de mi equipo",
    name: "Nombre",
    needsAttention: "Requiere atención",
    ordersWithNearArrivalDate: "Compras de coches con llegada inminente",
    nextNRows: (n: number) => `Siguientes ${n} filas`,
    newLeasing: "Nuevo leasing",
    newPlateNumber: "Nuevo numero de placa",
    newOrder: "Nuevo compra",
    no: "No",
    noDriver: "Sin conductor",
    noFileSelected: "Ningún archivo seleccionado",
    noHighwayPasses: "Sin telepeajes",
    noDueDate: "Sin vencimiento",
    noDueDateSelected: "Ningún vencimiento seleccionado",
    noItemsFound: "No se han encontrado elementos.",
    noLeasing: "Sin leasing",
    noMilegaes: "Sin kilometrajes",
    notes: "Notas",
    noTasksToDo: "No hay actividades por hacer",
    notFound: "No encontrado",
    notifications: "Notificaciones",
    notifyDriver: "Notificar al conductor",
    notInUse: "No en uso",
    notPlannedPurchase: "Compra no planificada",
    notRequired: "No requerido",
    notSettled: "No saldado",
    number: "Número",
    count: "Recuento",
    open: "Abierto",
    ok: "Ok",
    on: "el",
    openingDate: "Fecha de apertura",
    operation: "Operación",
    optional: "Opcional",
    optionals: "Opcionales",
    order: "Orden",
    orderImperativ: "Pedir",
    orderCar: "Pedir coche",
    orderDate: "Fecha de pedido",
    ordered: "Pedido",
    overview: "Resumen",
    overwrite: "Sobrescribir",
    otherAssignmentType: "Parque de coches / No en uso",
    others: "Otros",
    parking: "Aparcamiento",
    permissions: "Permisos",
    phoneNumber: "Número de teléfono",
    phoneNumberIsInvalid: "El número de teléfono no es válido para este país",
    picture: "Imagen",
    plannedArrivalDate: "Fecha de llegada prevista",
    plugInHybrid: "Híbrido enchufable",
    pkw: "Pkw",
    pkwAccountingNumber: "Número contable Pkw",
    pkwAccountingNumberShort: "Pkw Num. cont.",
    pkwSuperior: "Pkw superior",
    pkwSuperiorAccountingNumber: "Número contable Pkw superior",
    pkwSuperiorAccountingNumberShort: "Pkw Sup. Num. cont.",
    pkwUp: "Pkw Up",
    pkwUpAccountingNumber: "Número contable Pkw Up",
    pkwUpAccountingNumberShort: "Pkw Up Num. cont.",
    positionCode: "Código de posición",
    positionName: "Nombre de la posición",
    preferredLanguage: "Idioma preferido",
    preview: "Vista previa",
    previousEndDateLowerStartDate: "Introduce una fecha de fin anterior mayor que su fecha de inicio.",
    previousEndDateUndefined: "Establece una fecha de fin para el registro anterior.",
    previousNRows: (n: number) => `Filas anteriores ${n}`,
    purchasePriceInputForm: "Precio de compra (sin IVA - con IVA - moneda)",
    priceWithVat: "Precio con IVA",
    priceWithoutVat: "Precio sin IVA",
    pricePerUnit: "Precio por unidad",
    private: "Privado",
    privateCar: "Coche privado",
    productCategory: "Categoría de producto",
    productCode: "Código de producto",
    productType: "Tipo de producto",
    purchase: "Compra",
    purchaseDate: "Fecha de compra",
    purchaseType: "Compra / Leasing",
    quantity: "Cantidad",
    read: "Leer",
    reAssignment: "Re asignación",
    recipient: "Destinatario",
    resourceNotFound: "No se ha encontrado el recurso que buscas.",
    returned: "Devuelto al leasing",
    regionSalesManager: "Responsable de ventas regional",
    registrationDate: "Fecha de matriculación",
    rejectImages: "Rechazar imágenes",
    rejectImagesForm:
      "Las imágenes cargadas serán rechazadas y eliminadas. El conductor será notificado por correo electrónico de que se requieren nuevas imágenes. Puede proporcionar un mensaje para incluir en el correo electrónico:",
    reloadData: "Recargar datos",
    remove: "Quitar",
    rename: "Renombrar",
    renameDocument: "Renombrar documento",
    reopen: "Reabrir",
    reopenWorkflow: "Reabrir workflow",
    renewalDate: "Fecha de renovación",
    responsible: "Responsable",
    responsibleCode: "Código responsable",
    roles: "Roles",
    rolesAndPermissions: "Roles y permisos",
    salesRole: "Rol de ventas",
    save: "Guardar",
    saving: "Guardando",
    search: "Buscar",
    rejected: "Rechazado",
    select: "Seleccionar",
    selectAll: "Seleccionar todo",
    selectACountry: "Selecciona un país",
    selectDate: "Seleccionar fecha",
    selectFile: "Seleccionar archivo",
    selectFlag: "Seleccionar bandera",
    semiramisOrganizationCode: "Código de organización Semiramis",
    sendDelegation: "Enviar delegación",
    send: "Enviar",
    sendEmail: "Enviar email",
    setAsDone: "Marcar como hecho",
    inProgress: "En curso",
    setAsNotRequired: "Marcar como no requerido",
    setAsTodo: "Marcar como por hacer",
    setIsoCode: "Establecer código ISO",
    setNotInUse: "Establecer como no en uso",
    setSubsidiaryIsoCode: "Establecer código ISO de la filial",
    settings: "Configuración",
    settled: "Saldado",
    setToday: "Establecer hoy",
    showArrivalNotification: "Mostrar notificación de fecha de llegada",
    showToUser: "Mostrar al usuario",
    sold: "Vendido",
    someCostCenterPeriodsOverlap: "Algunos períodos de los centros de coste se superponen.",
    sortAscending: "Ordenar ascendente",
    sortDescending: "Ordenar descendente",
    spanish: "Español",
    startDate: "Fecha de inicio",
    startDateBeforeEndDatePrevRecord: "Introduce una fecha de inicio posterior al fin del registro anterior.",
    startDateEqualEndDate: "Introduce una fecha de inicio anterior a la fecha de fin.",
    startDateEqualPrevEndDate: "Fecha de inicio igual a la fecha de fin del registro anterior",
    startDistance: "Distancia inicial",
    state: "Estado",
    serviceFeeNet: "Tarifa de servicio neta",
    serviceFeeGross: "Tarifa de servicio bruta",
    since: "Desde",
    soldDate: "Fecha de venta",
    subsidiaryRequiredForAgreement: "La creación del acuerdo requiere asociar el coche a una filial.",
    subsidiaryRequiredForDelegation: "La creación de una delegación requiere asociar el coche a una filial.",
    subsidiaryHasCustomTasks: "La filial tiene actividades personalizadas",
    subsidiaryUsesDefaultTasks: "La filial utiliza actividades predeterminadas",
    tasks: "Actividades",
    taskCancelled: "Tarea cancelada - no se requiere acción",
    teamCar: "Coche del equipo",
    teamMembers: "Miembros del equipo",
    technicalSale: "Técnico comercial",
    telepass: "Telepeaje",
    testEnvironment: "Entorno de prueba",
    time: "Hora",
    to: "A",
    toApprove: "",
    toAssign: "Por aprobar",
    todo: "Por hacer",
    tolls: "Peajes",
    toOrder: "Por pedir",
    total: "Total",
    type: "Tipo",
    typeOfUse: "Tipo de uso",
    unassigned: "No asignado",
    unit: "Unidad",
    until: "Hasta",
    userCostCenters: "Centros de coste del usuario",
    userCostCentersHasImportErrorsText: {
      prefix: "La importación de los centros de coste de usuario desde bValue (Provisius) contiene errores. Haz clic",
      linkTitle: "aquí",
      suffix: "para listarlos y corregirlos.",
    },
    usersWithImportErrors: "Usuarios con errores de importación",
    userWithoutCostCenter: "Usuario sin centro de coste",
    update: "Actualizar",
    uploadedAt: "Subido el",
    uploadedBy: "Subido por",
    uploadImages: "Subir imágenes",
    uploadImagesRequired: "Subir las imágenes requeridas",
    vat: "IVA",
    vatCode: "Código IVA",
    vatDeductionCode: "Código de deducción de IVA",
    vatServiceCode: "Código de servicio de IVA",
    vehicleIdentificationNumber: "Número de identificación del vehículo",
    version: "Versión",
    versions: "Versiones",
    versionNumber: "Número de versión",
    viewImages: "Ver imágenes",
    waitingForImageApproval: "Esperando la aprobación de las imágenes",
    waitingUser: "Esperando usuario",
    view: "Mostrar",
    visibleToUser: "Visible para el usuario",
    visibleToUserInfo:
      "Este campo permite seleccionar un usuario con quien compartir la fecha de vencimiento. De forma predeterminada, el usuario actualmente asignado al vehículo está disponible para su selección. Si la fecha de vencimiento se compartió previamente con otro usuario, tanto el usuario asignado actualmente como el usuario con quien se compartió anteriormente estarán disponibles para su selección.",
    warningCloseEntities: (from: string, to: string) =>
      `El período definido (${from}-${to}) entra en conflicto con otros registros. Al confirmar, ${to} se aplicará como fecha de finalización para los registros listados arriba.`,
    warningReOpenEntities: (from: string, to: string) =>
      `El período seleccionado (${from}-${to}) entra en conflicto con otros registros. Seleccione las casillas de los registros que desea trasladar a la nueva filial. Al confirmar, ${to} se aplicará como fecha de finalización para los registros seleccionados y como fecha de inicio para los nuevos.`,
    when: "Cuándo",
    workflowCancelled: "Workflow cancelado. Puede reabrirlo usando el botón de abajo.",
    workflowImages: "Imágenes del workflow",
    yes: "Sí",
    year: "Año",

    endDateCantMatchStartDate: "La fecha de fin no puede coincidir con la fecha de inicio",
    endDateBeforeStartDate: "La fecha de fin no puede ser anterior a la fecha de inicio",
    endDateAfterCurrentAssignmentStartDate:
      "La fecha de fin no puede ser posterior a la fecha de inicio de la asignación actual",
    startDateFallsInCurrentAssignmentPeriod: "La fecha de inicio cae dentro del período de la asignación actual",
    endDateFallsInCurrentAssignmentPeriod: "La fecha de fin cae dentro del período de la asignación actual",
    endDateAfterStartDateNextRec: "Introduce una fecha de fin anterior a la fecha de inicio del siguiente registro.",
    endDateEqualNextStartDate: "Fecha de fin igual a la fecha de inicio del siguiente registro",
  },
};
export default es;
